import type { ProjectCanonicalIdentity } from "./components/ProjectIcon";

/** Where a project's icon comes from, and the sentence that says so. */
export interface ProjectIconProvenance {
  readonly kind: "cmmd" | "repository";
  readonly description: string;
  /**
   * Always false today, and stated rather than implied. Neither provenance is
   * writable from this page, and a row that looked editable would take a choice
   * it cannot save — the same failure the Model and Workspace rows guard.
   */
  readonly editableHere: boolean;
}

/**
 * Which system owns this project's icon.
 *
 * Decision 9 lists an icon row, and the tempting build is a Forge-side icon
 * field. That would be wrong. A CMMD-backed project already owns its identity —
 * an uploaded image or an emoji — and Forge keeping a second copy is the mirror
 * this repo has already deleted once: a local table that only ever held what
 * THIS install recorded, so it looked authoritative exactly when it was stale.
 *
 * So the row reports provenance instead of claiming ownership. The distinction
 * that matters is presence of a CMMD identity, NOT whether that identity has an
 * icon set: a CMMD project with neither an image nor an emoji is still CMMD's
 * to set, and telling its owner to edit a file in their repository would send
 * them to the wrong system entirely.
 */
export function resolveProjectIconProvenance(
  canonicalIdentity: ProjectCanonicalIdentity | undefined,
): ProjectIconProvenance {
  if (canonicalIdentity !== undefined) {
    return {
      kind: "cmmd",
      description: "This project's icon comes from its CMMD project. Change it there.",
      editableHere: false,
    };
  }

  return {
    kind: "repository",
    description: "Discovered from a favicon in this repository. Add or change one to update it.",
    editableHere: false,
  };
}
